import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, Slider, Stack, ToggleButton, Typography } from '@mui/material'
import { range, template } from 'lodash'
import i18n from 'simple-react-i18n'
import { ENTRY_SETTINGS_FREQUENCY } from 'agriAdministration/constants/AgriConstants'
import { getDayList } from 'utils/DateUtil'
import SimpleInput, { INPUT_TYPES } from 'components/forms/SimpleInput'
import { disabledBorderInputColor, disabledColor, lightBlue, mainBlue, veryLightBlue, whiteColor } from 'utils/constants/ColorTheme'
import { MAIN_RADIUS } from 'utils/constants/Theme'

const NB_DAYS_BY_WEEK = 7

const NB_DAYS_BY_FREQUENCY = {
    [ENTRY_SETTINGS_FREQUENCY.WEEKLY]: NB_DAYS_BY_WEEK,
    [ENTRY_SETTINGS_FREQUENCY.MONTHLY]: 31,
}

const getDayColors = ({ isReference, inRange }) => {
    if (isReference) return { border: mainBlue, background: mainBlue, text: whiteColor }
    if (inRange) return { border: lightBlue, background: veryLightBlue, text: mainBlue }
    return { border: disabledBorderInputColor, background: whiteColor, text: disabledColor }
}

const TerritorySettingsEntryRange = ({
    frequency,
    referenceDay,
    daysBefore = 0,
    daysAfter = 0,
    onChange = () => {},
    disabled = false,
}) => {
    const isWeekly = frequency === ENTRY_SETTINGS_FREQUENCY.WEEKLY
    const nbDays = NB_DAYS_BY_FREQUENCY[frequency] ?? NB_DAYS_BY_WEEK
    const maxSpan = nbDays - 1

    const dayLabels = useMemo(() => (isWeekly
        ? getDayList().map(({ label }) => label)
        : range(1, nbDays + 1).map(day => `${day}`)
    ), [isWeekly, nbDays])

    const refIndex = useMemo(() => (isWeekly
        ? getDayList().findIndex(({ value }) => value === referenceDay)
        : referenceDay - 1
    ), [isWeekly, referenceDay])

    const days = useMemo(() => range(nbDays).map(index => {
        const distance = (index - refIndex + nbDays) % nbDays
        return {
            distance,
            label: dayLabels[index],
            isReference: distance === 0,
            inRange: distance !== 0 && (distance <= daysAfter || (nbDays - distance) <= daysBefore),
        }
    }), [nbDays, refIndex, dayLabels, daysAfter, daysBefore])

    const marks = useMemo(() => range(-maxSpan, nbDays)
        .filter(value => isWeekly || value % 5 === 0)
        .map(value => {
            if (value === 0) return { value, label: dayLabels[refIndex] }
            if (Math.abs(value) === maxSpan) return { value, label: value > 0 ? `+${value}` : `${value}` }
            return { value }
        }), [maxSpan, isWeekly, dayLabels, refIndex])

    const summary = useMemo(() => {
        if (!daysBefore && !daysAfter) {
            return template(i18n.entryNoRangeOnX)({ day: dayLabels[refIndex] })
        }
        return template(i18n.entryOpenFromXToX)({
            from: dayLabels[(refIndex - daysBefore + nbDays) % nbDays],
            to: dayLabels[(refIndex + daysAfter) % nbDays],
            nbDays: daysBefore + daysAfter + 1,
        })
    }, [daysBefore, daysAfter, dayLabels, refIndex, nbDays])

    if (refIndex < 0 || refIndex >= nbDays) {
        return null
    }

    const clampRange = (before, after, hasMovedAfter) => {
        const clampedBefore = Math.min(Math.max(before || 0, 0), maxSpan)
        const clampedAfter = Math.min(Math.max(after || 0, 0), maxSpan)
        if (clampedBefore + clampedAfter <= maxSpan) {
            return { daysBefore: clampedBefore, daysAfter: clampedAfter }
        }
        return hasMovedAfter
            ? { daysBefore: maxSpan - clampedAfter, daysAfter: clampedAfter }
            : { daysBefore: clampedBefore, daysAfter: maxSpan - clampedBefore }
    }

    const onClickDay = ({ distance, isReference, inRange }) => {
        if (isReference) {
            onChange({ daysBefore: 0, daysAfter: 0 })
            return
        }
        const distanceBefore = nbDays - distance

        if (inRange) {
            if (distance <= daysAfter) {
                onChange(clampRange(daysBefore, distance - 1, true))
            } else {
                onChange(clampRange(distanceBefore - 1, daysAfter, false))
            }
            return
        }

        const gapAfter = Math.abs(distance - daysAfter)
        const gapBefore = Math.abs(distanceBefore - daysBefore)
        const extendsAfter = gapAfter === gapBefore ? distance <= distanceBefore : gapAfter < gapBefore
        if (extendsAfter) {
            onChange(clampRange(daysBefore, distance, true))
        } else {
            onChange(clampRange(distanceBefore, daysAfter, false))
        }
    }

    return (
        <Grid container size={12} spacing={2}>
            <Grid container size={6} spacing={1}>
                <Grid size={6}>
                    <SimpleInput
                        title={i18n.daysBeforeReferenceDay}
                        type={INPUT_TYPES.int}
                        value={`${daysBefore}`}
                        onChange={v => onChange(clampRange(parseInt(v), daysAfter, false))}
                        min={0}
                        disabled={disabled}
                        data-cy='entryRangeDaysBefore'
                    />
                </Grid>
                <Grid size={6}>
                    <SimpleInput
                        title={i18n.daysAfterReferenceDay}
                        type={INPUT_TYPES.int}
                        value={`${daysAfter}`}
                        onChange={v => onChange(clampRange(daysBefore, parseInt(v), true))}
                        min={0}
                        disabled={disabled}
                        data-cy='entryRangeDaysAfter'
                    />
                </Grid>
                <Grid size={12} padding={4}>
                    <Slider
                        value={[-daysBefore, daysAfter]}
                        onChange={(_, [left, right], activeThumb) => onChange(clampRange(-left, right, activeThumb === 1))}
                        min={-maxSpan}
                        max={maxSpan}
                        step={1}
                        marks={marks}
                        disableSwap
                        disabled={disabled}
                        valueLabelDisplay='auto'
                        valueLabelFormat={v => dayLabels[(refIndex + v + nbDays) % nbDays]}
                        data-cy='entryRangeSlider'
                    />
                </Grid>
            </Grid>
            <Grid size={6}>
                <Stack spacing={0.5}>
                    <Grid container columns={NB_DAYS_BY_WEEK} spacing={0.5}>
                        {days.map((day, index) => {
                            const { border, background, text } = getDayColors(day)
                            return (
                                <Grid key={index} size={1}>
                                    <ToggleButton
                                        value={index}
                                        selected={day.isReference || day.inRange}
                                        disabled={disabled}
                                        onClick={() => onClickDay(day)}
                                        title={day.isReference ? i18n.entryRangeResetHint : undefined}
                                        sx={{
                                            width: '100%',
                                            padding: '2px 4px',
                                            textTransform: 'none',
                                            borderRadius: MAIN_RADIUS,
                                            fontWeight: day.isReference ? 'bold' : 'normal',
                                            '&, &.Mui-selected, &.Mui-disabled': {
                                                borderColor: border,
                                                backgroundColor: background,
                                                color: text,
                                            },
                                            '&:hover, &.Mui-selected:hover': {
                                                borderColor: mainBlue,
                                                backgroundColor: day.isReference ? mainBlue : veryLightBlue,
                                                opacity: day.isReference ? 0.85 : 1,
                                            },
                                        }}
                                    >
                                        {day.label}
                                    </ToggleButton>
                                </Grid>
                            )
                        })}
                    </Grid>
                    <Typography variant='italicSubtitle'>
                        {`${summary} — ${template(i18n.entryRangeMaxTotal)({ used: daysBefore + daysAfter, max: maxSpan })}`}
                    </Typography>
                    {!disabled && (
                        <Typography variant='italicSubtitle'>{i18n.entryRangeClickHint}</Typography>
                    )}
                </Stack>
            </Grid>
        </Grid>
    )
}

TerritorySettingsEntryRange.propTypes = {
    frequency: PropTypes.number,
    referenceDay: PropTypes.number,
    daysBefore: PropTypes.number,
    daysAfter: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
}

export default TerritorySettingsEntryRange
